// src/DataService/EmailTemplateService.ts
import { refreshSession } from "./apiClient";
const API_URL = process.env.REACT_APP_API_URL;

/* =========================================================
   DTOs
========================================================= */
export interface EmailTemplateDto {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  type: string;
  isActive: boolean;
  createdAt?: string;
}

export interface CreateEmailTemplateDto {
  name: string;
  subject: string;
  htmlContent: string;
  type: string;
  isActive: boolean;
}

export type SendEmailResult = {
  ok: boolean;
  scheduled: boolean;
  message?: string;
  jobCount?: number;
  jobIds?: number[];
};

export type RecipientDto = {
  id: string;
  name: string;
  email: string;
  source?: "user" | "newslist" | "freebie";
  subscriberId?: string | null;
};

/* =========================================================
   Helper for session refresh
========================================================= */
async function fetchWithRefresh(input: RequestInfo, init?: RequestInit) {
  const doFetch = () =>
    fetch(input, {
      credentials: "include",
      ...init,
    });

  let res = await doFetch();

  if (res.status === 401) {
    const refreshed = await refreshSession();
    if (!refreshed) return res;

    res = await doFetch();
  }

  return res;
}

/* =========================================================
   MAIN SERVICE – ALL IN ONE
========================================================= */

const EmailTemplateService = {
  /* -----------------------------
       RECIPIENTS
  ----------------------------- */

  getUserRecipients: async (): Promise<RecipientDto[]> => {
    try {
      const res = await fetchWithRefresh(`${API_URL}/email/recipients/users`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) return [];
      return await res.json();
    } catch {
      return [];
    }
  },

  getNewsListRecipients: async (): Promise<RecipientDto[]> => {
    try {
      const res = await fetchWithRefresh(`${API_URL}/email/recipients/newslist`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) return [];
      return await res.json();
    } catch {
      return [];
    }
  },

  /* -----------------------------
       SEND EMAIL
  ----------------------------- */

  sendEmail: async (payload: FormData): Promise<SendEmailResult> => {
    const doSend = () =>
      fetch(`${API_URL}/email/send`, {
        method: "POST",
        credentials: "include",
        body: payload,
      });

    try {
      let res = await doSend();

      if (res.status === 401) {
        const refreshed = await refreshSession();
        if (!refreshed) return { ok: false, scheduled: false };

        res = await doSend();
      }

      const body = await res.json().catch(() => ({} as any));

      return {
        ok: res.ok,
        scheduled: Boolean(body?.scheduled),
        message: body?.message,
        jobCount: body?.jobCount,
        jobIds: body?.jobIds,
      };
    } catch (err) {
      console.error("sendEmail error:", err);
      return { ok: false, scheduled: false };
    }
  },

  /* -----------------------------
       TEMPLATE CRUD
  ----------------------------- */

  getAll: async (): Promise<EmailTemplateDto[]> => {
    try {
      const res = await fetchWithRefresh(`${API_URL}/EmailTemplate`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) return [];
      return await res.json();
    } catch {
      return [];
    }
  },

  getVisible: async (): Promise<EmailTemplateDto[]> => {
    try {
      const res = await fetchWithRefresh(`${API_URL}/EmailTemplate/visible`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) return [];
      return await res.json();
    } catch {
      return [];
    }
  },

  getFreebieRecipients: async (): Promise<RecipientDto[]> => {
    try {
      const res = await fetchWithRefresh(`${API_URL}/email/recipients/freebies`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) return [];
      return await res.json();
    } catch {
      return [];
    }
  },

  getPublic: async (): Promise<EmailTemplateDto[]> => {
    try {
      const res = await fetch(`${API_URL}/EmailTemplate/public`, {
        method: "GET",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) return [];
      return await res.json();
    } catch {
      return [];
    }
  },

  getById: async (id: string): Promise<EmailTemplateDto | null> => {
    try {
      const res = await fetchWithRefresh(`${API_URL}/EmailTemplate/${id}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  },

  create: async (
    dto: CreateEmailTemplateDto
  ): Promise<EmailTemplateDto | null> => {
    try {
      const res = await fetchWithRefresh(`${API_URL}/EmailTemplate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dto),
      });

      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  },

  update: async (
    id: string,
    dto: CreateEmailTemplateDto
  ): Promise<EmailTemplateDto | null> => {
    try {
      const res = await fetchWithRefresh(`${API_URL}/EmailTemplate/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dto),
      });

      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  },

  delete: async (id: string): Promise<boolean> => {
    try {
      const res = await fetchWithRefresh(`${API_URL}/EmailTemplate/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      return res.ok;
    } catch {
      return false;
    }
  },
};

export default EmailTemplateService;
